import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MediaCategory } from '../models/media/media-category.model';
import { MediaCategoryNames } from '../models/media/media-category-name';
import { FormArray } from '@angular/forms';
import { SaleTemplate, SaleTemplateRequest } from '../models/media/sale-template.model';
import { LookupModel } from '../models/common/lookup.model';
import { SpinnerService } from '../shared-new/services/spinner.service';
import { MediaGroup } from '../models/media/media-group.model';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  public baseUrl = environment.api2BaseUrl;
  public onError: BehaviorSubject<string> = new BehaviorSubject('');
  public serviceParameter = {
    'Content-Type': 'application/json; charset=utf-8',
    Accept: 'application/json',
    Authorization: '',
  };

  public constructor(private readonly http: HttpClient, private spinnerService: SpinnerService) { }

  public saveMediaCategoryNames(categories: MediaCategoryNames[]): Observable<MediaCategoryNames[]> {
    this.spinnerService.addRequest('saveMediaCategoryNames');
    return this.http.post<MediaCategoryNames[]>(`${this.baseUrl}/api/settings/SaveMediaCategoryNames`, categories, {
      withCredentials: false,
      observe: "response"
    }).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        this.spinnerService.popRequest('saveMediaCategoryNames');
        return throwError(error);
      }),
      map((res) => {
        this.spinnerService.popRequest('saveMediaCategoryNames');
        return res.body;
      })
    );
  }

  public saveMediaCategory(category: MediaCategory): Observable<Boolean> {
    this.spinnerService.addRequest('saveMediaCategory');
    return this.http.post<Boolean>(`${this.baseUrl}/api/settings/SaveMediaCategory`, category, {
      withCredentials: false,
      observe: "response"
    }).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        this.spinnerService.popRequest('saveMediaCategory');
        return throwError(error);
      }),
      map((res) => {
        this.spinnerService.popRequest('saveMediaCategory');
        return res.body;
      })
    );
  }

  public getMediaCategoryById(mediaCategoryId: number): Observable<MediaCategory> {
    this.spinnerService.addRequest('getMediaCategoryById');
    return this.http.get<MediaCategory>(
      `${this.baseUrl}/api/settings/${mediaCategoryId}/MediaCategory`, {
      withCredentials: false,
      observe: "response"
    }
    ).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        this.spinnerService.popRequest('getMediaCategoryById');
        return throwError(error);
      }),
      map((res) => {
        this.spinnerService.popRequest('getMediaCategoryById');
        return res.body;
      })
    );
  }

  downloadMediaCategorycsv(): Observable<Blob> {
  const url = `${this.baseUrl}/api/settings/ExportMediaCategoriesCSV`;

  return this.http.post(url, {}, {
    responseType: 'blob'
  });
} 

  public deleteMediaCategoryById(mediaCategoryId: number): Observable<Boolean> {
    this.spinnerService.addRequest('deleteMediaCategoryById');
    return this.http.get<Boolean>(
      `${this.baseUrl}/api/settings/${mediaCategoryId}/DeleteMediaCategory`, {
      withCredentials: false,
      observe: "response"
    }
    ).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        this.spinnerService.popRequest('deleteMediaCategoryById');
        return throwError(error);
      }),
      map((res) => {
        this.spinnerService.popRequest('deleteMediaCategoryById');
        return res.body;
      })
    );
  }

  public getMediaGroups(): Observable<MediaGroup[]> {
    this.spinnerService.addRequest('getMediaGroups');
    return this.http.get<MediaGroup[]>(
      `${this.baseUrl}/api/settings/MediaGroups`, {
      withCredentials: false,
      observe: "response"
    }
    ).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        this.spinnerService.popRequest('getMediaGroups');
        return throwError(error);
      }),
      map((res) => {
        this.spinnerService.popRequest('getMediaGroups');
        return res.body;
      })
    );
  }

  public saveSaleTemplate(saleTemplate: SaleTemplateRequest): Observable<boolean> {
    this.spinnerService.addRequest('saveSaleTemplate');
    return this.http.post<boolean>(`${this.baseUrl}/api/settings/SaveSaleTemplates`, saleTemplate, {
      withCredentials: false,
      observe: "response"
    }).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        this.spinnerService.popRequest('saveSaleTemplate');
        return throwError(error);
      }),
      map((res) => {
        this.spinnerService.popRequest('saveSaleTemplate');
        return res.body;
      })
    );
  }

  public SaveMediaCategorySorting(model: any[]): Observable<MediaCategory[]> {
    this.spinnerService.addRequest('SaveMediaCategorySorting');
    return this.http.post<MediaCategory[]>(`${this.baseUrl}/api/settings/SaveMediaCategorySorting`, model, {
      withCredentials: false,
      observe: "response"
    }).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        this.spinnerService.popRequest('SaveMediaCategorySorting');
        return throwError(error);
      }),
      map((res) => {
        this.spinnerService.popRequest('SaveMediaCategorySorting');
        return res.body;
      })
    );
  }

  public getMediaCategories(saleTemplateId?: number): Observable<MediaCategory[]> {
    const id = saleTemplateId ?? 0;
    this.spinnerService.addRequest('getMediaCategories');
    return this.http.get<MediaCategory[]>(
      `${this.baseUrl}/api/settings/${id}/MediaCategories`, {
      withCredentials: false,
      observe: "response"
    }
    ).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        this.spinnerService.popRequest('getMediaCategories');
        return throwError(error);
      }),
      map((res) => {
        this.spinnerService.popRequest('getMediaCategories');
        return res.body;
      })
    );
  }

  public getSaleTemplates(): Observable<SaleTemplate[]> {
    this.spinnerService.addRequest('getSaleTemplates');
    return this.http.get<SaleTemplate[]>(
      `${this.baseUrl}/api/settings/SaleTemplates`, {
      withCredentials: false,
      observe: "response"
    }
    ).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        this.spinnerService.popRequest('getSaleTemplates');
        return throwError(error);
      }),
      map((res) => {
        this.spinnerService.popRequest('getSaleTemplates');
        return res.body;
      })
    );
  }

  public getSaleTemplateById(saleTemplateId: number): Observable<SaleTemplate> {
    this.spinnerService.addRequest('getSaleTemplateById');
    return this.http.get<SaleTemplate>(
      `${this.baseUrl}/api/settings/${saleTemplateId}/SaleTemplate`, {
      withCredentials: false,
      observe: "response"
    }
    ).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        this.spinnerService.popRequest('getSaleTemplateById');
        return throwError(error);
      }),
      map((res) => {
        this.spinnerService.popRequest('getSaleTemplateById');
        return res.body;
      })
    );
  }

  public getMediaCategoryNamesAsFormArray(mediaCategoryId: number): Observable<FormArray> {
    this.spinnerService.addRequest('getMediaCategoryNamesAsFormArray');
    return this.getMediaCategoryNames(mediaCategoryId).pipe(map((albums: MediaCategoryNames[]) => {
      // Maps all the albums into a formGroup defined in the album.model.ts
      const fgs = albums.map(MediaCategoryNames.asFormGroup);
      this.spinnerService.popRequest('getMediaCategoryNamesAsFormArray');
      return new FormArray(fgs);
    }));
  }

  public getMediaCategoryNames(mediaCategoryId: number): Observable<MediaCategoryNames[]> {
    this.spinnerService.addRequest('getMediaCategoryNames');
    return this.http.get<MediaCategoryNames[]>(
      `${this.baseUrl}/api/settings/${mediaCategoryId}/MediaCategoryNames`, {
      withCredentials: false,
      observe: "response"
    }
    ).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        this.spinnerService.popRequest('getMediaCategoryNames');
        return throwError(error);
      }),
      map((res) => {
        this.spinnerService.popRequest('getMediaCategoryNames');
        return res.body;
      })
    );
  }

  public deleteMediaCategoryNames(mediaCategoryNameId: number): Observable<MediaCategoryNames[]> {
    this.spinnerService.addRequest('deleteMediaCategoryNames');
    return this.http.get<MediaCategoryNames[]>(
      `${this.baseUrl}/api/settings/${mediaCategoryNameId}/DeleteMediaCategoryNames`, {
      withCredentials: false,
      observe: "response"
    }
    ).pipe(
      catchError((error: Error) => {
        this.onError.next(error.message);
        this.spinnerService.popRequest('deleteMediaCategoryNames');
        return throwError(error);
      }),
      map((res) => {
        this.spinnerService.popRequest('deleteMediaCategoryNames');
        return res.body;
      })
    );
  }
}
